import { gql } from 'graphql-tag';

export const afterEffectsSchema = gql`
  """
  Active After Effects composition surfaced to the Frame.io panel.
  """
  type AfterEffectsComposition {
    """
    Composition id on the host.
    """
    id: ID
    """
    Composition display name.
    """
    name: String
    """
    Composition exports metadata.
    """
    metadata: [CompositionExportsXMPMetadata!]!
  }

  """
  Active After Effects project surfaced to the Frame.io panel.
  """
  type AfterEffectsProject {
    """
    Project id on the host.
    """
    id: ID
    """
    Project display name.
    """
    name: String
  }

  extend type Query {
    """
    Currently active After Effects project, if any.
    """
    activeAfterEffectsProject: AfterEffectsProject
    """
    Currently active composition in the active project, if any.
    """
    activeComposition: AfterEffectsComposition
    getAeExportSettings: AeExportSettings
    getAeRenderTemplates: AeRenderTemplates
    getRenderQueueItemCount: Int!
    getAllCompositionExportsXmpMetadata: [CompositionExportsXMPMetadata!]!
  }

  enum AeExportRange {
    entireComposition
    compositionWorkArea
  }

  type AeExportSettings {
    assetFolderId: String
    assetFolderName: String
    exportMarkersAsCommentsEnabled: Boolean
    localCopyEnabled: Boolean
    localFolderPath: String
    outputModuleTemplate: String
    renderSettingTemplate: String
    range: AeExportRange
  }

  input AeExportSettingsInput {
    assetFolderId: String
    assetFolderName: String
    exportMarkersAsCommentsEnabled: Boolean
    localCopyEnabled: Boolean
    localFolderPath: String
    outputModuleTemplate: String
    renderSettingTemplate: String
    range: AeExportRange
  }

  type AeRenderTemplates {
    outputModuleTemplates: [String!]!
    renderSettingsTemplates: [String!]!
  }

  extend type Mutation {
    updateAeExportSettings(input: AeExportSettingsInput!): AeExportSettings
    exportRenderQueue(jobId: ID!, skipDone: Boolean): ExportRenderPayload
    saveExportXmpMetadataToComposition(
      compositionId: ID!
      xmpMetadata: CompositionXMPMetadataInput!
    ): AfterEffectsComposition

    removeCompositionExportMetadata(
      assetId: ID!
      compositionId: ID
    ): [AfterEffectsComposition!]!
  }

  type ExportRenderPayload {
    jobId: ID!
    items: [ExportRenderItem!]!
  }

  type ExportRenderItem {
    compositionId: ID!
    compositionName: String!
    outputPath: String!
    index: Int!
    """
    Work area start (Render Queue item Time Span start) in microseconds on the
    composition timeline. Null when the host plugin predates this field or the
    value cannot be read.
    """
    workAreaStart: Float
    """
    Work area duration (Render Queue item Time Span duration) in microseconds.
    Null when the host plugin predates this field or the value cannot be read.
    """
    workAreaDuration: Float
    """
    Composition frame rate (frames per second) for the rendered comp. Used when
    mapping marker timestamps to frame-aligned comment times. Null when the
    host cannot read it.
    """
    framesPerSecond: Float
  }

  type Subscription {
    exportRenderEvents: ExportRenderEventsPayload!
  }

  enum RenderJobFailedReason {
    ALREADY_RENDERING
    NO_OUTPUT
    NO_PROJECT
    NOTHING_TO_RENDER
    UNKNOWN
    UNSUPPORTED_RENDER_OUTPUT
  }

  type RenderJobFailedPayload {
    jobId: ID!
    reason: RenderJobFailedReason!
  }

  type RenderJobCompleteItem {
    compositionId: ID!
    filePath: String!
    fileName: String!
    """
    Work area start (Render Queue item Time Span start) in microseconds on the
    composition timeline — the start of what was actually rendered to file.
    Null when the host plugin predates this field or the value cannot be read.
    """
    workAreaStart: Float
    """
    Work area duration (Render Queue item Time Span duration) in microseconds —
    the duration of what was actually rendered to file. Null when the host
    plugin predates this field or the value cannot be read.
    """
    workAreaDuration: Float
    """
    Composition frame rate (frames per second) for the rendered comp. Null when
    the host cannot read it.
    """
    framesPerSecond: Float
  }

  type RenderJobCompletePayload {
    jobId: ID!
    items: [RenderJobCompleteItem!]!
  }

  type RenderJobStartedPayload {
    jobId: ID!
    totalItems: Int!
    completedItems: Int!
  }

  union ExportRenderEventsPayload =
      RenderJobFailedPayload
    | RenderJobCompletePayload
    | RenderJobStartedPayload

  type CompositionExportsXMPMetadata {
    assetId: ID!
    compositionId: ID
    compositionName: String
    versionId: ID
    projectId: ID!
    accountId: ID!
    exportedAt: Float!
    """
    Render Queue time-span start on the composition timeline (µs) for this export.
    Used with comment timestamps (asset-relative) for marker sync playhead alignment.
    Null when unset or from a host predating this field.
    """
    workAreaStart: Float
    """
    Render Queue time-span duration (µs) for this export. With workAreaStart,
    defines the rendered comp-time window for export-markers-as-comments (skip
    markers outside the span; shift timestamps into asset time). Marker sync
    playhead/marker offset uses workAreaStart only.
    """
    workAreaDuration: Float
  }

  input CompositionXMPMetadataInput {
    assetId: ID!
    versionId: ID
    projectId: ID!
    accountId: ID!
    """
    When set, comment timestamps for this asset are shifted by this many µs onto
    the composition timeline (same basis as marker export).
    """
    workAreaStart: Float
    workAreaDuration: Float
  }
`;
